import sum from 'lodash/sum';
import uniqBy from 'lodash/uniqBy';
import { createSlice } from '@reduxjs/toolkit';
// utils
import { selectFrom, selectOne, callRPC, insertInto } from '../../services/databaseService';
import { supabase } from '../../services/supabase';
import { ProductState, CartItem, Product } from '../../@types/product';
import { collection, query, where, getDocs } from 'firebase/firestore';
//
import { dispatch } from '../store';
import { getCurrentTenantVenueParams } from '../../utils/tenantVenue';
import { InventoryItems, InventoryState } from 'src/@types/inventory';
import { Order, OrderState } from 'src/@types/order';

// ----------------------------------------------------------------------

const initialState: InventoryState = {
    isLoading: false,
    error: null,
    inventory: [],
    statement: [],
    history: [],
    isOpenModal: false,
    sortBy: null,
    filters: {
      type: ['All'],
      category: 'All',
      priceRange: [0, 200],
    }
  };

const slice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET PRODUCT HISTORY
    getHistorySuccess(state, action) {
      state.isLoading = false;
      state.history = action.payload;
    },

    // GET SUMMARY
    getSummarySuccess(state, action) {
      state.isLoading = false;
      state.inventory = action.payload;
    },

    // GET STATEMENT
    getStatementSuccess(state, action) {
      state.isLoading = false;
      state.statement = action.payload;
    },

    //  SORT & FILTER PRODUCTS
    sortByProducts(state, action) {
      state.sortBy = action.payload;
    },

    filterProducts(state, action) {
    //   state.filters.gender = action.payload.gender;
    //   state.filters.category = action.payload.category;
    //   state.filters.colors = action.payload.colors;
    //   state.filters.priceRange = action.payload.priceRange;
    //   state.filters.rating = action.payload.rating;
    },

     // OPEN MODAL
     openModal(state) {
      state.isOpenModal = true;
    },

    // CLOSE MODAL
    closeModal(state) {
      state.isOpenModal = false;
    },

    // STOP LOADING
    stopLoading(state) {
      state.isLoading = false;
    },

    // // MANAGE INVENTORY
    // increaseQuantity(state, action) {
    //   const productId = action.payload;
    //   const updateCart = state.checkout.cart.map((product) => {
    //     if (product.id === productId) {
    //       return {
    //         ...product,
    //         quantity: product.quantity + 1,
    //       };
    //     }
    //     return product;
    //   });

    //   state.checkout.cart = updateCart;
    // },

    // decreaseQuantity(state, action) {
    //   const productId = action.payload;
    //   const updateCart = state.checkout.cart.map((product) => {
    //     if (product.id === productId) {
    //       return {
    //         ...product,
    //         quantity: product.quantity - 1,
    //       };
    //     }
    //     return product;
    //   });

    //   state.checkout.cart = updateCart;
    // },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
//   increaseQuantity,
//   decreaseQuantity,
  sortByProducts,
  filterProducts,
  openModal,
  closeModal,
} = slice.actions;

// ----------------------------------------------------------------------

// Helper function to get venue_user_id from current auth user
async function getCurrentVenueUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: venueUser } = await supabase
      .from('venue_users')
      .select('id')
      .eq('auth_user_id', user.id)
      .single();

    return venueUser?.id || null;
  } catch (error) {
    console.error('Error getting venue_user_id:', error);
    return null;
  }
}

export function getInventoryStatement() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Get current venue_user_id
      const venueUserId = await getCurrentVenueUserId();
      if (!venueUserId) {
        dispatch(slice.actions.stopLoading());
        return;
      }

      // Get tenant venue parameters (organization_id or venue_id)
      const { organization_id, venue_id } = getCurrentTenantVenueParams();

      // Use RPC wrapper function with organization/venue filtering
      const statement = await callRPC<any[]>('get_inventory_statement_filtered', {
        p_venue_user_id: venueUserId,
        p_organization_id: organization_id,
        p_venue_id: venue_id,
      });

      console.log('Inventory Statement:', statement);

      dispatch(slice.actions.getStatementSuccess(statement));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getProductInventoryHistory(product_id: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to get product inventory history with joins
      const history = await callRPC<any[]>('get_product_inventory_history', {
        p_product_id: parseInt(product_id),
      });

      dispatch(slice.actions.getHistorySuccess(history));
      return history;
    } catch (error: any) {
      console.error('Error fetching product inventory history:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function getInventorySummary() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Get current venue_user_id
      const venueUserId = await getCurrentVenueUserId();
      if (!venueUserId) {
        dispatch(slice.actions.stopLoading());
        return;
      }

      // Get tenant venue parameters (organization_id or venue_id)
      const { organization_id, venue_id } = getCurrentTenantVenueParams();

      // Use RPC wrapper function with organization/venue filtering
      const summary = await callRPC<any[]>('get_inventory_summary_filtered', {
        p_venue_user_id: venueUserId,
        p_organization_id: organization_id,
        p_venue_id: venue_id,
      });

      dispatch(slice.actions.getSummarySuccess(summary));
      return summary;
    } catch (error: any) {
      console.error('Error fetching inventory summary:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function createInventoryEntry(data: {
  venue_id: string;
  product_id: number;
  quantity: number;
  cost: number;
  venue_user_id?: string;
  comments?: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use secure RPC function (uses auth.uid() internally, no p_venue_user_id needed)
      const result = await callRPC<{
        inventory_entry_id: number;
        new_quantity: number;
      }>('update_inventory', {
        p_venue_id: data.venue_id,
        p_product_id: data.product_id,
        p_quantity: data.quantity,
        p_cost: data.cost,
        p_comments: data.comments || null,
      });

      // Fetch updated inventory entry
      const entry = await selectOne<any>('inventory_entry', { id: result.inventory_entry_id });

      dispatch(slice.actions.getStatementSuccess([entry]));
      return result;
    } catch (error: any) {
      console.error('Error creating inventory entry:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function bulkCreateInventoryEntry(data: Array<{
  venue_id: string;
  product_id: number;
  quantity: number;
  cost: number;
  venue_user_id?: string;
  comments?: string;
}>) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Create multiple inventory entries
      const entryPromises = data.map((entry) =>
        callRPC('update_inventory', {
          p_venue_id: entry.venue_id,
          p_product_id: entry.product_id,
          p_quantity: entry.quantity,
          p_cost: entry.cost,
          p_venue_user_id: entry.venue_user_id || null,
          p_comments: entry.comments || null,
        })
      );

      const results = await Promise.all(entryPromises);

      // Fetch all created entries
      const entryIds = results.map((r) => r.inventory_entry_id);
      const entries = await Promise.all(
        entryIds.map((id) => selectOne<any>('inventory_entry', { id }))
      );

      dispatch(slice.actions.getStatementSuccess(entries.filter(Boolean)));
      return results;
    } catch (error: any) {
      console.error('Error bulk creating inventory entries:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function exportInventoryStatement(data: {
  venue_id?: string;
  start_date?: string;
  end_date?: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Query inventory entries with filters
      const query: any = {
        select: `
          *,
          venue_products!inventory_entry_product_id_fkey (
            id,
            name,
            price
          ),
          venues!inventory_entry_venue_id_fkey (
            id,
            name
          )
        `,
        order: { column: 'created_at', ascending: false },
      };

      if (data.venue_id) {
        query.eq = { venue_id: data.venue_id };
      }

      if (data.start_date) {
        query.gte = { created_at: data.start_date };
      }

      if (data.end_date) {
        query.lte = { created_at: data.end_date };
      }

      const statement = await selectFrom<any>('inventory_entry', query);

      dispatch(slice.actions.getStatementSuccess(statement));
      return statement;
      // // TODO: Implement CSV/Excel export on frontend using the fetched data
      // link.setAttribute('download', 'exported_data.xlsx');
      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);

      // dispatch(slice.actions.stopLoading());
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}
